import type { FC } from 'react';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { Redirect, Route, useHistory } from '@compat/router';
import CookieWarning from '@components/CookieWarning';
import FlocktoryDataLayer from '@components/Flocktory/FlocktoryDataLayer';
import FlocktoryUser from '@components/Flocktory/FlocktoryUser';
import API from '@api';
import { v1 } from 'uuid';
import scrollTo from '@utils/scrollTo';
import { when } from 'mobx';
import { observer } from 'mobx-react';
import type { LayoutProps } from './Router.type';
import type { PartnerLoginOrRegisterMutationVariables } from '@schema/cart';
import { cartToken } from '@store/CartStore';
import { takeTinkoffSourceCart } from '@utils/tinkoffSourceCart';
import usePreviousValue from '@customHooks/usePreviousValue';
import { prepareSeoRequestUrl, useSeoQuery } from '@queries/index';
import { useStores } from './stores';

export const REFERAL_PARTNER_ID_KEY = 'referalPartnerId';
export const REFERAL_COURIER_ID_KEY = 'referalCourierId';

type CacheDataRouteProps = LayoutProps & {
  canonicalUrl?: string;
};

// Волна E (docs/MIGRATION_ROUTER.md §3 п.7): Layout здесь НЕ рендерится —
// шапка/подвал живут в LayoutShell (__root, поверх Outlet). Страница
// рендерится через универсальный compat-Route (v5-пропсы + контекст).
const CacheDataRoute: FC<CacheDataRouteProps> = observer(props => {
  const { component, path: routePath } = props;
  const { userStore, cartStore, catalogStore, modalsStore, seoStore } =
    useStores();

  const history = useHistory();
  const isBarMenu = /\/bar-?menu/.test(history.location.pathname);

  useEffect(() => {
    if (isBarMenu) {
      document.body.classList.add('is-bar-menu');
    } else {
      document.body.classList.remove('is-bar-menu');
    }

    return () => {
      document.body.classList.remove('is-bar-menu');
    };
  }, [isBarMenu]);

  useEffect(() => {
    Promise.all([
      cartStore.getShops(),
      cartStore.getActiveStoreCityWithFields()
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const partnerLoginOrRegister = useCallback(
    ({
      state,
      code,
      session_state
    }: PartnerLoginOrRegisterMutationVariables): void => {
      if (state && code && session_state) {
        const referalPartnerId = localStorage.getItem(REFERAL_PARTNER_ID_KEY);
        const referalCourierId = localStorage.getItem(REFERAL_COURIER_ID_KEY);
        const partnerId = referalPartnerId;
        const courierId = referalCourierId;
        const params = {
          state,
          code,
          session_state,
          partnerId: partnerId ? +partnerId : undefined,
          courierId: courierId ? +courierId : undefined
        };
        API.partnerLoginOrRegister(params).then(res => {
          if (res.partnerLoginOrRegister?.people) {
            const cookieId = res.partnerLoginOrRegister?.people?.cookie_id;
            const cartCookieId =
              res.partnerLoginOrRegister?.people?.cartCookieId || v1();
            const settingsItem =
              res.partnerLoginOrRegister?.people?.settingsItem;
            const { setSettings, getFullUserInfo } = userStore;
            const { initCartStore } = cartStore;

            if (settingsItem) {
              setSettings(settingsItem);
            }

            if (referalPartnerId) {
              localStorage.removeItem('referalPartnerId');
            }

            if (cookieId) {
              const { changeAuthToMerge } = modalsStore;
              const savedSourceCartToken = takeTinkoffSourceCart();
              const sourceCartToken =
                savedSourceCartToken || cartStore?.cart?.cookieId || cartToken;

              const afterMerge = (): void => {
                history.push('/');
              };

              getFullUserInfo(cookieId, cartCookieId)
                .then(async () => {
                  await initCartStore({
                    pathname: location.pathname,
                    isUserLoggedIn: true,
                    token: cartCookieId,
                    menuPeopleId: userStore.user?.id
                  });
                })
                .then(() => {
                  const targetCartToken =
                    cartStore?.cart?.cookieId || cartCookieId;

                  if (!sourceCartToken || sourceCartToken === targetCartToken) {
                    afterMerge();
                    return;
                  }

                  changeAuthToMerge(
                    {
                      targetCartToken,
                      sourceCartToken,
                      registration: false
                    },
                    afterMerge
                  );
                });
            }
          }
        });
      }
    },
    [cartStore, history, modalsStore, userStore]
  );

  useEffect(() => {
    if (routePath?.includes('/auth/tinkoff')) {
      // @ts-expect-error https://developer.mozilla.org/en-US/docs/Web/API/URL/searchParams
      const params = new URL(document.location).searchParams;
      const state = params.get('state');
      const code = params.get('code');
      const session_state = params.get('session_state');

      if (state && code && session_state) {
        partnerLoginOrRegister({ state, code, session_state });
      }
    }
  }, [partnerLoginOrRegister, routePath]);

  const prevLocationPathname = usePreviousValue(history.location.pathname);

  useEffect(() => {
    if (component) {
      const { catalogState, setCatalogState, setPositionY } = catalogStore;

      if (
        typeof routePath === 'string' &&
        !routePath.toLowerCase().includes('/catalog/:id') &&
        !routePath.includes('/product/:id')
      ) {
        if (catalogState?.count) {
          setCatalogState(null);
        }
        if (catalogState?.positionY) {
          setPositionY(0);
        }
      }
    }
  }, [catalogStore, component, routePath]);

  const isAmp = useCallback((): boolean | string => {
    const pathname = history.location.pathname;

    if (!pathname.includes('product/')) {
      return false;
    }

    const id = pathname.split('product/')[1];
    const idInt = parseInt(id);

    if (isNaN(idInt) || +id !== idInt) {
      return false;
    }

    return `https://www.delikateska.ru/product/${idInt}/amp`;
  }, [history.location.pathname]);

  // Мета-теги страницы: ключ кэша (normalizeSeoUrl) повторяет старую
  // логику compareUrlWithoutParams / compareRecipesPage
  const {
    data: seoData,
    isError: isSeoError,
    isPlaceholderData: isSeoPlaceholderData,
    dataUpdatedAt: seoDataUpdatedAt
  } = useSeoQuery(history.location.pathname);

  const state = useMemo(() => {
    // Ошибка meta.get — фолбэк с 404, как раньше в catch
    if (isSeoError) {
      return {
        lastModified: '',
        siteH1: '',
        amp: false as boolean | string,
        title: 'Деликатеска.ру',
        keywords: '',
        description: '',
        relCanonical: null,
        code: '404' as string | number | null,
        ogType: '',
        ogImage: '',
        noindex: false
      };
    }

    // Данных ещё нет — пустое состояние, как initial state раньше
    if (!seoData) {
      return {
        siteH1: '',
        amp: false as boolean | string,
        title: '',
        keywords: '',
        description: '',
        relCanonical: null,
        lastModified: '',
        code: null as string | number | null,
        ogType: '',
        ogImage: '',
        noindex: false
      };
    }

    const {
      siteTitle,
      siteKeywords,
      siteDescription,
      pageLastModified,
      relCanonical = null,
      pageAmp = false,
      'og:type': ogType = false,
      'og:image': ogImage = false,
      siteH1,
      noindex
    } = seoData;

    let title = siteTitle;

    // For prerender
    if (!title && !location.href.includes('prerender')) {
      title = 'Деликатеска.ру';
    }

    return {
      amp: isAmp() || pageAmp,
      title,
      keywords: siteKeywords,
      description: siteDescription,
      relCanonical,
      lastModified: pageLastModified,
      code: seoData.status,
      siteH1,
      ogType: typeof ogType === 'string' && ogType.length > 0 ? ogType : '',
      ogImage: typeof ogImage === 'string' && ogImage.length > 0 ? ogImage : '',
      noindex: !!noindex
    };
  }, [seoData, isSeoError, isAmp]);

  // Уведомление пререндера о том, что мета получена + синхронизация
  // seoStore (H1 и bottomText читают SeoWrapper/h1 и bottomText).
  // console.log('meta.ready') не удалять!
  useEffect(() => {
    // isPlaceholderData: при переходе на новую страницу query через
    // keepPreviousData отдаёт мету предыдущей — её в seoStore не пишем,
    // иначе на новой странице мелькнёт чужой H1
    if (!seoData || isSeoPlaceholderData) {
      return;
    }

    console.log('meta.ready');

    seoStore?.setData({
      url: prepareSeoRequestUrl(history.location.pathname),
      siteH1: seoData.siteH1 || '',
      bottomText: seoData.bottomText || ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seoDataUpdatedAt, isSeoPlaceholderData, history.location.pathname]);

  useEffect(() => {
    const newPath = history.location.pathname;
    const oldPath = prevLocationPathname;

    if (compareParentUrl(newPath, oldPath)) {
      scrollTo.reset();
    }
  }, [history.location.pathname, prevLocationPathname]);

  useEffect(() => {
    const disposer = when(
      () => {
        const { isLoad } = userStore;
        const { isLoadCart } = cartStore;
        return isLoad && isLoadCart;
      },

      () => {
        const { isUserLoggedIn, isUserLoading } = userStore;
        const { cart, rebindCartPid } = cartStore;

        if (
          !isUserLoading &&
          isUserLoggedIn &&
          !(cart?.people && cart.people.bill)
        ) {
          rebindCartPid();
        }
      }
    );
    return disposer;
  }, [cartStore, userStore]);

  useEffect(() => {
    const disposer = when(
      () => {
        const { isUserLoading, isUserLoggedIn } = userStore;
        return !isUserLoading && isUserLoggedIn;
      },

      () => {
        const { setIsConsentModalAgreed } = modalsStore;
        setIsConsentModalAgreed(true);
      }
    );
    return disposer;
  }, [modalsStore, userStore]);

  const compareParentUrl = (thisUrl: string, nextUrl: string): boolean =>
    thisUrl.split('/')[1] !== nextUrl.split('/')[1];

  // ЭТАП 1 · Шаг 3 — SSR-safe: берём локацию из compat-history (привязан к
  // роутеру запроса), а не из window (на сервере его нет). Даёт реальный путь.
  const url = history.location.pathname;
  const hash = history.location.hash === '#/' ? '' : history.location.hash;
  const isLoginHash = hash === '#login' || hash?.includes('#login');

  const {
    amp,
    code,
    description,
    lastModified,
    ogImage,
    ogType,
    relCanonical,
    siteH1,
    title,
    noindex
  } = state;

  const isErrorPage = Number(code) >= 400;
  const canonical =
    relCanonical || (isErrorPage || noindex ? null : props.canonicalUrl);

  const Component = component;
  // Только Route-пропсы — layout-флаги читает LayoutShell из staticData
  const routePassProps = {
    path: props.path,
    exact: props.exact,
    strict: props.strict,
    sensitive: props.sensitive,
    location: props.location
  };

  return (
    <Fragment>
      {!isLoginHash &&
        (url !== url.replace(/\/{2,}/g, '/') ||
          (url.substr(-1) === '/' && url !== '/')) && (
          <Redirect
            to={`${url.replace(/\/{2,}/g, '/').replace(/\/$/, '')}${hash}`}
          />
        )}

      {/* ЭТАП 2 — мета БЕЗ Helmet: React 19 нативно хойстит <title>/<meta>/
          <link> в <head> — и в SSR-потоке, и на клиенте (Helmet писал теги
          только в живой document.head, поэтому SSR-HTML шёл без меты).
          Данные — тот же useSeoQuery (REST meta.get); на сервере запрос
          прогрет ssrPrefetch.globalQueries → на первой отрисовке мета уже
          в HTML, на SPA-переходах React обновляет теги сам. */}
      <>
        <title>{title}</title>
        {/* {keywords != null && keywords?.length > 0 && (
            <meta name="keywords" content={keywords} />
          )} */}
        {description != null && description?.length > 0 && (
          <meta name='description' content={description} />
        )}
        {code && (typeof code === 'string' || typeof code === 'number') && (
          <meta
            name='render:status_code'
            content={code ? `${code}` : `${200}`}
          />
        )}
        <meta name='render:last_modified' content={lastModified} />
        {noindex && <meta name='robots' content='noindex' />}
        {ogType != null &&
          ogType.length > 0 && [
            <meta
              key='og:0'
              property='og:url'
              content={`https://www.delikateska.ru${location.pathname}`}
            />,
            <meta key='og:1' property='og:type' content={ogType} />,
            <meta key='og:2' property='og:title' content={title} />,
            <meta key='og:3' property='og:description' content={description} />,
            ogImage != null && ogImage.length > 0 ? (
              <meta key='og:4' property='og:image' content={ogImage} />
            ) : null,
            <meta
              key='og:5'
              property='og:site_name'
              content='Деликатеска.ру'
            />,
            <meta key='og:6' property='og:locale' content='ru_RU' />
          ]}
        {!!canonical && <link rel='canonical' href={canonical} />}
        {amp && typeof amp === 'string' && (
          <link rel='amphtml' href={`${amp}`} />
        )}
      </>

      <FlocktoryUser />
      <FlocktoryDataLayer />
      {/* Страница: универсальный compat-Route даёт v5-пропсы
          (history/location/match) и CompatMatchContext вложенным
          хукам. Header/Footer рендерит LayoutShell в __root (волна E). */}
      <Route
        {...routePassProps}
        render={routeProps => <Component {...routeProps} seoH1={siteH1} />}
      />

      {!isBarMenu && <CookieWarning />}
    </Fragment>
  );
});

export default CacheDataRoute;
